import { useEffect, useState } from 'react'
import {
  GeocodeHit,
  geocodeAddress,
  looksLikeStreetAddress,
  resolveLocally,
} from '../utils/geocode'

export type DestinationStatus = 'empty' | 'resolved' | 'searching' | 'notFound' | 'error'

export interface DestinationState {
  destination: GeocodeHit | null
  status: DestinationStatus
}

/**
 * Resolves a typed delivery address to coordinates. ZIPs and bare city names
 * resolve instantly offline; full street addresses hit the live geocoder
 * after a short debounce.
 */
export function useDestination(query: string): DestinationState {
  const local = resolveLocally(query)
  const [remote, setRemote] = useState<GeocodeHit | null>(null)
  const [status, setStatus] = useState<DestinationStatus>('empty')

  useEffect(() => {
    const trimmed = query.trim()

    if (!trimmed) {
      setRemote(null)
      setStatus('empty')
      return
    }

    if (local) {
      setRemote(null)
      setStatus('resolved')
      return
    }

    if (!looksLikeStreetAddress(trimmed) && trimmed.length < 5) {
      setStatus('notFound')
      return
    }

    const controller = new AbortController()
    setStatus('searching')

    const timer = window.setTimeout(() => {
      geocodeAddress(trimmed, controller.signal)
        .then((hit) => {
          if (controller.signal.aborted) return
          setRemote(hit)
          setStatus(hit ? 'resolved' : 'notFound')
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted || (error as Error).name === 'AbortError') return
          setRemote(null)
          setStatus('error')
        })
    }, 500)

    return () => {
      controller.abort()
      window.clearTimeout(timer)
    }
  }, [query, local?.label])

  return { destination: local ?? remote, status: local ? 'resolved' : status }
}
